import {
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import {
  CarrierType,
  CarrierVisitDirection,
  CarrierVisitStatus,
  JourneyCategory,
} from '@planning/app/api'
import { LocationCodeAutoComplete } from '@planning/components'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import { useValidateBulkMove } from '@planning/hooks/useValidateBulkMove'
import { BulkEditSelectionDto } from '@planning/hooks/useVesselVisitTableActions'
import { StowageInstructionsAutocomplete } from '@planning/pages/Order/V2/organisms/StowageInstructionsAutocomplete'
import { RailVisitBulkEditViewStore } from '@planning/pages/RailVisit/stores/RailVisitBulkEditViewStore'
import { VesselVisitBulkEditViewStore } from '@planning/pages/VesselVisit/Stores/VesselVisitBulkEditViewStore'
import { StowageInstructionAutocompleteViewStore } from '@planning/stores/stowageInstruction/StowageInstructionAutocompleteViewStore'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useLocation } from 'react-router'
import { EditableField } from '../EditableField/EditableField'
import { useBulkEditAlertText } from '../hooks/useBulkEditAlertText'
import { OutboundSubFormStore } from './OutboundSubFormStore'

interface OutboundSubFormSectionProps {
  store: OutboundSubFormStore
  viewStore: VesselVisitBulkEditViewStore | RailVisitBulkEditViewStore
  category?: JourneyCategory
  defaultCarrierType?: CarrierType
}

export const OutboundSubFormSection = observer(
  ({ store, viewStore, category, defaultCarrierType }: OutboundSubFormSectionProps) => {
    const { t } = useTranslate()
    const location = useLocation()
    const { validate } = useValidateBulkMove()

    const { outboundAlertText } = useBulkEditAlertText(viewStore)

    const { data } = location.state || {}
    const selectedContainerNumbers = data?.map((i: BulkEditSelectionDto) => i.containerNumber) || []

    const stowageInstructionsAutocompleteViewStore = useMemo(
      () => new StowageInstructionAutocompleteViewStore(),
      [],
    )

    const carrierTypeOptions = useMemo(() => {
      const allCarrierTypeOptions = [
        { value: CarrierType.Universal, label: t('unknown', 'Unknown') },
        { value: CarrierType.Train, label: t('train', 'Train') },
        { value: CarrierType.Truck, label: t('truck', 'Truck') },
        { value: CarrierType.Vessel, label: t('vessel', 'Vessel') },
      ]

      if (category === JourneyCategory.Import) {
        return allCarrierTypeOptions.filter(option => option.value !== CarrierType.Vessel)
      }

      return allCarrierTypeOptions
    }, [category, t])

    const effectiveCarrierType = store.carrierType.value || defaultCarrierType

    return (
      <Card variant='outlined'>
        <CardHeader
          title={t('outboundTransport', 'Outbound transport')}
          subheader={outboundAlertText}
        />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(!category ||
              category === JourneyCategory.None ||
              category === JourneyCategory.Import ||
              category === JourneyCategory.Export) && (
              <Grid size={{ xs: 12, md: 4 }}>
                <EditableField
                  label={t('visit', 'Visit')}
                  store={store.carrierVisit}
                  choice={store.visitChoice}
                  onChoiceChange={choice => {
                    store.setVisitChoice(choice)
                    store.carrierType.setValue(undefined)
                    store.carrierVisit.setValue(undefined)

                    if (choice === 'edit') {
                      store.carrierType.setValue(defaultCarrierType || CarrierType.Universal)
                    }
                  }}
                  dataCy='bulk-edit-outbound-carrier-visit'
                >
                  {() => <></>}
                </EditableField>
              </Grid>
            )}

            {store.visitChoice === 'edit' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ width: '100%' }} data-cy='bulk-edit-outbound-carrier-type'>
                    <Typography variant='subtitle2'>{t('carrierType', 'Carrier type')}</Typography>
                    <FormControl
                      fullWidth
                      error={store.carrierType.isTouched && !!store.carrierType.error}
                    >
                      <Select
                        value={store.carrierType.value}
                        onChange={e => {
                          store.carrierType.setValue(e.target.value as CarrierType)
                          store.carrierVisit.setValue(undefined)
                        }}
                        data-cy='change-transport-select'
                      >
                        {carrierTypeOptions?.map(option => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            data-cy={`bulk-edit-outbound-carrier-type-value-option-${option.value}`}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {store.carrierType.isTouched && store.carrierType.error && (
                        <FormHelperText error>
                          {t(store.carrierType.error, store.carrierType.error)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Box>
                </Grid>

                {effectiveCarrierType !== CarrierType.Universal &&
                  effectiveCarrierType !== CarrierType.Truck && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Box sx={{ width: '100%' }} data-cy='bulk-edit-outbound-carrier-type'>
                        <Typography variant='subtitle2'>{t('newVisit', 'New visit')}</Typography>
                        {store.carrierType.value === CarrierType.Vessel && (
                          <VesselVisitAutoComplete
                            cargoType='Container'
                            enablePortal
                            required
                            hideBlockedForEdit
                            label={''}
                            error={!!store.carrierVisit.error}
                            helperText={store.carrierVisit.error ? store.carrierVisit.error : ''}
                            handleOnChange={async entity => {
                              const isValid = await validate(
                                store.carrierVisit,
                                CarrierVisitDirection.Outbound,
                                entity!.id,
                                selectedContainerNumbers,
                              )
                              if (isValid) {
                                store.carrierVisit.setValue(entity)
                              }
                            }}
                          />
                        )}

                        {store.carrierType.value === CarrierType.Train && (
                          <RailVisitAutoComplete
                            cargoType='Container'
                            enablePortal
                            required
                            label={''}
                            error={!!store.carrierVisit.error}
                            helperText={store.carrierVisit.error ? store.carrierVisit.error : ''}
                            statuses={[
                              CarrierVisitStatus.Expected,
                              CarrierVisitStatus.InOperation,
                              CarrierVisitStatus.Arrived,
                            ]}
                            handleOnChange={async entity => {
                              const isValid = await validate(
                                store.carrierVisit,
                                CarrierVisitDirection.Outbound,
                                entity!.id,
                                selectedContainerNumbers,
                              )
                              if (isValid) {
                                store.carrierVisit.setValue(entity)
                              }
                            }}
                          />
                        )}
                      </Box>
                    </Grid>
                  )}
              </>
            )}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {(category === JourneyCategory.Export ||
              category === JourneyCategory.Transshipment) && (
              <Grid size={{ xs: 12, md: 4 }}>
                <EditableField
                  label={t('portOfDischarge', 'Port of discharge')}
                  store={store.portOfDischarge}
                  dataCy='bulk-edit-port-of-discharge'
                >
                  {(value, onChange) => (
                    <LocationCodeAutoComplete
                      defaultValue={value}
                      onChange={value => onChange(value)}
                      label={t('portOfDischarge', 'Port of discharge')}
                      dataCy='portOfDischarge-autocomplete'
                    />
                  )}
                </EditableField>
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <EditableField
                label={t('finalDestination', 'Final destination')}
                store={store.finalDestination}
                dataCy='bulk-edit-final-destination'
              >
                {(value, onChange) => (
                  <LocationCodeAutoComplete
                    defaultValue={value}
                    onChange={value => onChange(value)}
                    label={t('finalDestination', 'Final destination')}
                    multiple={false}
                    dataCy='finalDestination-autocomplete'
                  />
                )}
              </EditableField>
            </Grid>

            {(!category ||
              category === JourneyCategory.None ||
              category === JourneyCategory.Import ||
              category === JourneyCategory.Transshipment) && (
              <Grid size={{ xs: 12, md: 4 }}>
                <EditableField
                  label={t('inlandDestination', 'Inland destination')}
                  store={store.inlandDestination}
                  dataCy='bulk-edit-inland-destination'
                >
                  {(value, onChange) => (
                    <LocationCodeAutoComplete
                      defaultValue={value}
                      onChange={value => onChange(value)}
                      label={t('inlandDestination', 'Inland destination')}
                      multiple={false}
                      dataCy='inlandDestination-autocomplete'
                    />
                  )}
                </EditableField>
              </Grid>
            )}

            {(category === JourneyCategory.Export || category === JourneyCategory.Transshipment) &&
              effectiveCarrierType === CarrierType.Vessel && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <EditableField
                    label={t('stowageInstructions', 'Stowage instructions')}
                    store={store.stowageInstructions}
                    dataCy='bulk-edit-stowage-instructions'
                  >
                    {() => (
                      <StowageInstructionsAutocomplete
                        fieldStore={store.stowageInstructions}
                        autoCompleteStore={stowageInstructionsAutocompleteViewStore}
                      />
                    )}
                  </EditableField>
                </Grid>
              )}
          </Grid>

          {(category === JourneyCategory.Export ||
            category === JourneyCategory.Transshipment ||
            category === JourneyCategory.None) && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <EditableField
                  label={t('approvalForLoading', 'Approval for loading')}
                  dataCy='bulk-edit-loading-approval'
                  options={[
                    { value: true, label: t('yes', 'Yes') },
                    { value: false, label: t('no', 'No') },
                  ]}
                  store={store.approvalForLoading}
                  disableClear
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    )
  },
)
